import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useStore } from "./StoreLayout";
import api, { money, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, Package, Star, Plus, Lock } from "lucide-react";

export default function Storefront() {
  const { store, slug, addToCart, isCustomer } = useStore();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/stores/${slug}/products`, {
        params: { search: search || undefined, category_id: cat || undefined },
      });
      setData(data);
    } catch (e) {
      setData({ products: [], categories: [], show_pricing: false, can_order: false, error: true });
    }
  }, [slug, search, cat]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const add = (p) => { addToCart(p); toast.success(`${p.name} added to cart`); };

  return (
    <div>
      {store.banner_url ? (
        <div className="h-56 sm:h-72 w-full overflow-hidden relative">
          <img src={fileUrl(store.banner_url)} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/30 flex items-end">
            <div className="max-w-6xl mx-auto w-full px-6 pb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">{store.name}</h1>
              {store.tagline && <p className="text-white/85 mt-2 max-w-xl">{store.tagline}</p>}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-primary relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-10" />
          <div className="relative max-w-6xl mx-auto px-6 py-14">
            <h1 className="text-3xl sm:text-4xl font-bold text-primary-foreground tracking-tight">{store.name}</h1>
            {store.tagline && <p className="text-primary-foreground/85 mt-2 max-w-xl text-lg">{store.tagline}</p>}
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {!isCustomer && (
          <div className="mb-6 rounded-xl border border-primary/20 bg-primary/5 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-sm">Sign in to see your personalized pricing and place orders.</p>
            <Link to={`/store/${slug}/login`}><Button size="sm" className="rounded-full" data-testid="storefront-signin-cta">Sign in</Button></Link>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products..." className="pl-9 h-11" data-testid="storefront-search" />
          </div>
        </div>

        {data?.categories?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            <button onClick={() => setCat(null)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${!cat ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>All</button>
            {data.categories.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${cat === c.id ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`} data-testid={`category-chip-${c.id}`}>{c.name}</button>
            ))}
          </div>
        )}

        {!data ? (
          <p className="text-center text-muted-foreground py-16">Loading...</p>
        ) : data.products.length === 0 ? (
          <div className="text-center py-16">
            <Package className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
            <p className="font-medium">No products available</p>
          </div>
        ) : (
          <div className="grid gap-6 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {data.products.map((p) => (
              <div key={p.id} className="group rounded-xl border border-border bg-card overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-md" data-testid={`store-product-${p.id}`}>
                <Link to={`/store/${slug}/product/${p.id}`} className="block aspect-square bg-secondary overflow-hidden relative">
                  {p.images?.[0] ? <img src={fileUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    : <div className="w-full h-full flex items-center justify-center"><Package className="h-10 w-10 text-muted-foreground/40" /></div>}
                  {p.featured && <span className="absolute top-2 left-2 bg-amber-500 text-white rounded-full px-2 py-0.5 text-xs font-semibold flex items-center gap-1"><Star className="h-3 w-3 fill-white" /> Featured</span>}
                  {!p.in_stock && <span className="absolute top-2 right-2 bg-black/70 text-white rounded-full px-2 py-0.5 text-xs">Out of stock</span>}
                </Link>
                <div className="p-4">
                  <Link to={`/store/${slug}/product/${p.id}`}><h3 className="font-semibold text-sm truncate hover:text-primary transition-colors">{p.name}</h3></Link>
                  <div className="mt-2 flex items-center justify-between">
                    {data.show_pricing ? <span className="font-bold">{money(p.price)}</span> : <span className="text-xs text-muted-foreground flex items-center gap-1"><Lock className="h-3 w-3" /> Sign in</span>}
                    {data.can_order && p.in_stock && data.show_pricing && (
                      <Button size="icon" variant="secondary" className="h-8 w-8 rounded-full" onClick={() => add(p)} data-testid={`add-to-cart-${p.id}`}><Plus className="h-4 w-4" /></Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
